/**
 * PrCardTemplates — pure presentational cards for PR share posts.
 *
 * Each template accepts { pr, userName, previewW, previewH } and renders
 * a 9:16 or 1:1 shareable card. Used by PrCardGenerator.
 *
 * pr shape: { exerciseName, weight, reps, prType, prevBest }
 *   - weighted PR  → big yellow "{weight} KG" slam
 *   - reps PR (bodyweight, prType === 'reps' or weight <= 0) → big green "{reps} REPS" slam
 *
 * Visual language extends PRCelebration.js overlay — orange gradient,
 * trophy, big slam. Mirrors MilestoneCardTemplates structure.
 */

import React from 'react';
import { getExerciseInputMode } from '../lib/exerciseDisplay';

const FONT = "'Barlow Condensed', sans-serif";

function LogoLockup({ size = 22, primary = '#FF5C00', secondary = '#ffffff' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: size * 0.3, fontFamily: FONT }}>
      <div
        style={{
          width: size, height: size, borderRadius: size * 0.25,
          background: primary, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 900, fontSize: size * 0.7,
        }}
      >
        RS
      </div>
      <span
        style={{
          fontSize: size * 0.7, fontWeight: 800, letterSpacing: size * 0.03,
          textTransform: 'uppercase', color: secondary,
        }}
      >
        trainedby<span style={{ color: primary }}>RS</span>
      </span>
    </div>
  );
}

function BaseCard({ previewW, previewH, children }) {
  return (
    <div
      style={{
        width: '100%', height: '100%',
        background: 'linear-gradient(165deg, #1a0a00 0%, #0a0a0a 60%, #2a0a00 100%)',
        color: '#fff', fontFamily: FONT, position: 'relative', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div
        style={{
          position: 'absolute', top: -previewW * 0.3, right: -previewW * 0.3,
          width: previewW * 0.8, height: previewW * 0.8, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,92,0,0.40) 0%, transparent 70%)',
          filter: 'blur(4px)',
        }}
      />
      <div
        style={{
          position: 'absolute', bottom: -previewH * 0.18, left: -previewW * 0.25,
          width: previewW * 0.7, height: previewW * 0.7, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,140,66,0.18) 0%, transparent 70%)',
          filter: 'blur(6px)',
        }}
      />
      {children}
    </div>
  );
}

function HeaderRow({ pad }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: pad }}>
      <LogoLockup size={pad * 1.1} />
      <div
        style={{
          fontSize: pad * 0.48, fontWeight: 700, letterSpacing: pad * 0.1,
          textTransform: 'uppercase', color: '#FF5C00',
          padding: `${pad * 0.18}px ${pad * 0.5}px`,
          border: '1px solid rgba(255,92,0,0.5)', borderRadius: pad * 0.3,
        }}
      >
        Personal Record
      </div>
    </div>
  );
}

function FooterRow({ userName, pad }) {
  return (
    <div
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: pad, borderTop: '1px solid rgba(255,255,255,0.12)',
      }}
    >
      <span style={{ fontSize: pad * 0.5, color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
        trainedbyrs.com
      </span>
      {userName && (
        <span style={{ fontSize: pad * 0.5, color: 'rgba(255,255,255,0.6)' }}>
          @{userName}
        </span>
      )}
    </div>
  );
}

function TrophyIcon({ size }) {
  return (
    <div
      style={{
        width: size, height: size, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #FF5C00, #FF8C42)',
        boxShadow: `0 0 ${size * 0.6}px rgba(255,92,0,0.5), 0 0 ${size * 1.2}px rgba(255,92,0,0.2)`,
      }}
    >
      <svg
        width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none"
        stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      >
        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
        <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
        <path d="M4 22h16" />
        <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
        <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
        <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
      </svg>
    </div>
  );
}

function PrContent({ pr, previewW, pad }) {
  const weight = Number(pr?.weight ?? 0);
  const reps = Number(pr?.reps ?? 0);
  const prev = Number(pr?.prevBest ?? 0);
  // Continuous-cardio exercises have no kg/reps PR concept — never show a "× N REPS"
  // sub-line for them (would render "× 0 REPS"). They effectively never reach a PR card.
  const isCardioPr = getExerciseInputMode({ name: pr?.exerciseName }) === 'cardio';
  // Bodyweight exercises progress by reps, not load — show "{reps} REPS" in green.
  const isRepsPr = pr?.prType === 'reps' || (weight <= 0 && reps > 0);
  const slamColor = isRepsPr ? '#4ADE80' : '#FCD34D';
  const slamGlow = isRepsPr ? 'rgba(74,222,128,0.45)' : 'rgba(252,211,77,0.45)';
  const current = isRepsPr ? reps : weight;
  const showDiff = prev > 0 && current > prev;
  const diff = showDiff ? Math.round((current - prev) * 10) / 10 : null;
  const unit = isRepsPr ? 'REPS' : 'KG';
  return (
    <>
      <TrophyIcon size={previewW * 0.24} />
      <div
        style={{
          fontSize: previewW * 0.14, fontWeight: 900, textTransform: 'uppercase',
          letterSpacing: previewW * 0.004, textAlign: 'center', color: '#fff',
          lineHeight: 0.95, textShadow: '0 0 20px rgba(255,92,0,0.5)',
        }}
      >
        New PR!
      </div>
      <div
        style={{
          fontSize: previewW * 0.22, fontWeight: 900, color: slamColor,
          letterSpacing: previewW * 0.006, lineHeight: 1, textAlign: 'center',
          textShadow: `0 0 25px ${slamGlow}`,
        }}
      >
        {current} {unit}
      </div>
      {!isRepsPr && !isCardioPr && reps > 1 && (
        <div style={{ fontSize: previewW * 0.055, color: 'rgba(255,255,255,0.85)', fontWeight: 700 }}>
          × {reps} REPS
        </div>
      )}
      {isRepsPr && (
        <div style={{ fontSize: previewW * 0.045, color: 'rgba(255,255,255,0.7)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: previewW * 0.006 }}>
          Bodyweight
        </div>
      )}
      <div
        style={{
          fontSize: previewW * 0.052, color: 'rgba(255,255,255,0.85)',
          textAlign: 'center', textTransform: 'uppercase',
          letterSpacing: previewW * 0.008, maxWidth: '90%',
        }}
      >
        {pr?.exerciseName || ''}
      </div>
      {showDiff && (
        <div
          style={{
            marginTop: pad * 0.2,
            padding: `${pad * 0.25}px ${pad * 0.6}px`,
            borderRadius: pad * 0.3,
            background: 'rgba(255,92,0,0.15)',
            border: '1px solid rgba(255,92,0,0.35)',
            fontSize: previewW * 0.04, color: '#FF8C42', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 1,
          }}
        >
          +{diff} {unit} OVER PREVIOUS BEST
        </div>
      )}
    </>
  );
}

function PrStoryTemplate({ pr, userName, previewW, previewH }) {
  const pad = previewW * 0.06;
  return (
    <BaseCard previewW={previewW} previewH={previewH}>
      <HeaderRow pad={pad} />
      <div
        style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: pad, gap: pad * 0.5, zIndex: 1,
        }}
      >
        <PrContent pr={pr} previewW={previewW} pad={pad} />
      </div>
      <FooterRow userName={userName} pad={pad} />
    </BaseCard>
  );
}

function PrSquareTemplate({ pr, userName, previewW, previewH }) {
  const pad = previewW * 0.05;
  return (
    <BaseCard previewW={previewW} previewH={previewH}>
      <HeaderRow pad={pad} />
      <div
        style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: pad, gap: pad * 0.35, zIndex: 1,
        }}
      >
        <PrContent pr={pr} previewW={previewW} pad={pad} />
      </div>
      <FooterRow userName={userName} pad={pad} />
    </BaseCard>
  );
}

export const PR_TEMPLATES = {
  story: PrStoryTemplate,
  square: PrSquareTemplate,
};

export function templateFor(ratio) {
  return PR_TEMPLATES[ratio] || PR_TEMPLATES.story;
}
